import React, { useEffect, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Dealer_Mapping } from "../../../../Configuration/API_URL";
import axiosInstance from "../../../../CommonMethhods/axiosInstance";
import { decryptData } from "../../../../CommonMethhods/CommonMethods";
import arrow from "../../../../asset/images/gray-arrow.svg";
import uploadIcon from "../../../../asset/images/upload-file.svg";
import view from "../../../../asset/images/view-eyes.svg";
import download from "../../../../asset/images/download-brokrage.svg";
import rejectedIcon from "../../../../asset/images/crossIcon.svg";
import { formatDate, toSentenceCase } from "../../../../CommonMethhods/CommonMethods";

// Read-only view of a single (SIN) dealer mapping request - used for
// Pending / Approved / Rejected service requests.
function SingleRequestDetails({ batchId, status, rowData, summary }) {
  const request = rowData?.requests?.[0] || {};

  // "partial" is the same state as "pending" for the maker, so it is shown as Pending.
  const getStatusClass = (value = "") => {
    switch (value.toLowerCase()) {
      case "pending":
        return "status-pending";
      // case "partial":
      //   return "status-pending";
      case "approved":
        return "status-approved";
      case "rejected":
        return "status-rejected";
      case "draft":
        return "status-draft";
      case "sendback":
        return "status-sendback";
      default:
        return "";
    }
  };

  const getStatusLabel = (value = "") =>
    value.toLowerCase() === "partial" ? "Pending" : toSentenceCase(value);

  // Not falling back to localStorage "userName" here - SSO stores the
  // employee ID in it, so it showed the ID in place of the name.
  const employeeName =
    rowData?.makerName ||
    summary?.makerName ||
    request?.makerName ||
    "-";

  const employeeId =
    rowData?.makerId ||
    summary?.makerId ||
    localStorage.getItem("userId") ||
    "-";

  const clientNameCode = request?.clientName
    ? `${request.clientName}- ${request?.ucc || ""}`
    : request?.ucc || "-";

  const rejectionRemarks = Array.isArray(request?.rejectionReason)
    ? request.rejectionReason.filter(Boolean).join(", ")
    : request?.rejectionReason || "";

  const approvalDocLink = rowData?.approvalDocLink || "";

  // Old dealer code is optional at submission - when the maker left it blank
  // the backend uses the client's current mapping, so it isn't on the request.
  // While the request is still pending that current mapping is the old dealer.
  // (Once approved, the current mapping is the new dealer, so no lookup then.)
  const [activeMapping, setActiveMapping] = useState(null);
  const isPending = ["pending", "partial"].includes(status?.toLowerCase());

  useEffect(() => {
    // if (request?.oldDealerCode || !request?.ucc || !isPending) return;
    // Looked up even when a value was saved, so this page shows the same old
    // dealer as the mapping request form (the client's current mapping).
    if (!request?.ucc || !isPending) return;

    let isActive = true;

    axiosInstance
      .get(`${Dealer_Mapping.activeMapping}/${request.ucc}`)
      .then((response) => {
        const decrypted = JSON.parse(decryptData(response?.data));
        if (isActive && decrypted?.status === 0) {
          setActiveMapping(decrypted?.data || null);
        }
      })
      .catch((error) => console.log(error));

    return () => {
      isActive = false;
    };
  }, [request?.ucc, request?.oldDealerCode, isPending]);

  // const oldDealerCode = request?.oldDealerCode || activeMapping?.dealerCode || "";
  // const oldDealerName = request?.oldDealerName || activeMapping?.dealerName || "";
  // const oldBranch = request?.oldBranch || activeMapping?.branch || request?.branch;
  const oldDealerCode = activeMapping?.dealerCode || request?.oldDealerCode || "";
  const oldDealerName = activeMapping?.dealerName || request?.oldDealerName || "";
  const oldBranch = activeMapping?.branch || request?.oldBranch || request?.branch;

  const formatDealer = (code, name) =>
    code ? (name ? `${code} - ${name}` : code) : "-";

  // Category is the type the maker picked; "Others" carries the maker's own text.
  // Category is the type the maker picked; anything else is shown as "Others"
  // and the maker's explanation is in the remarks.
  // Note: this label is "Category" in the UI, not the backend's "reason" field name -
  // it's a derived display, see the "Category"/"Reason" naming note in BulkRequestDetails.jsx.
  const getReasonLabel = (value = "") => {
    const reasonText = value.trim();
    if (!reasonText) return "-";
    if (reasonText.toLowerCase() === "new mapping") return "New Mapping";
    if (reasonText.toLowerCase() === "update existing mapping") return "Update existing mapping";
    // return `Others - ${reasonText}`;
    return "Others";
  };

  const handleViewDoc = () => {
    if (!approvalDocLink) return;
    window.open(approvalDocLink, "_blank", "noopener,noreferrer");
  };

  const handleDownloadDoc = () => {
    if (!approvalDocLink) return;
    window.open(approvalDocLink, "_self", "noopener,noreferrer");
  };

  return (
    <>
      <div className="ckr-section">
        <div className="draft-header">
          <div className="status-content">
            <span className="ckr-section-title">{batchId}</span>
            {status ? (
              <span className={getStatusClass(status)}>
                {getStatusLabel(status)}
              </span>
            ) : null}
          </div>
        </div>

        <div className="dlr-request-info">
          <div className="dlr-info-item">
            <span className="dlr-info-label">Client Name &amp; Code</span>
            <span className="dlr-info-value">{clientNameCode}</span>
          </div>

          <div className="dlr-info-item">
            <span className="dlr-info-label">Employee Name</span>
            <span className="dlr-info-value">{employeeName}</span>
          </div>

          <div className="dlr-info-item">
            <span className="dlr-info-label">Employee ID</span>
            <span className="dlr-info-value">{employeeId}</span>
          </div>

          <div className="dlr-info-item">
            <span className="dlr-info-label">Effective Date</span>
            <span className="dlr-info-value">
              {request?.startDate ? formatDate(request.startDate) : "-"}
            </span>
          </div>
        </div>

        <span className="dlr-sub-title">Dealer Mapping Comparison</span>

        <div className="rm-comparison mb-20">
          <div className="rm-comparison-card old">
            <div className="comparison-title">OLD</div>

            <div className="comparison-row">
              <span>Dealer</span>
              {/* <strong>{formatDealer(oldDealerCode, oldDealerName)}</strong> */}
              <strong>
                {oldDealerCode ? formatDealer(oldDealerCode, oldDealerName) : "NA"}
              </strong>
            </div>

            <div className="comparison-row">
              <span>Branch</span>
              <strong>{oldBranch || "-"}</strong>
            </div>
          </div>

          <img src={arrow} alt="arrow" />

          <div className="rm-comparison-card new">
            <div className="comparison-title">NEW</div>

            <div className="comparison-row">
              <span>Dealer</span>
              <strong>{formatDealer(request?.newDealerCode, request?.newDealerName)}</strong>
            </div>

            <div className="comparison-row">
              <span>Branch</span>
              <strong>{request?.branch || "-"}</strong>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <span className="dlr-sub-title">Reason Type</span>
            <input
              type="text"
              className="rm-reason-input mb-20"
              value={getReasonLabel(request?.reason || summary?.reason)}
              readOnly
              disabled
            />
          </div>

          <div className="col-md-6">
            <span className="dlr-sub-title">Reason</span>
            <input
              type="text"
              className="rm-reason-input mb-20"
              value={request?.remarks || summary?.remarks || "-"}
              readOnly
              disabled
            />
          </div>
        </div>

        {rowData?.approvalDocFilename && (
          <>
            <span className="dlr-sub-title">Attachment</span>

            <div className="rm-attachment dlr-attachment">
              <OverlayTrigger
                placement="bottom"
                overlay={
                  <Tooltip id="file-tooltips">
                    {rowData?.approvalDocFilename}
                  </Tooltip>
                }
              >
                <div className="attachment-name">
                  <img src={uploadIcon} alt="upload icon" />
                  <span className="file-name">
                    {rowData?.approvalDocFilename}
                  </span>
                </div>
              </OverlayTrigger>

              <div className="attachment-actions">
                <button type="button" onClick={handleViewDoc}>
                  <img src={view} alt="view" className="view-icon" />
                  <span className="btn-title">View</span>
                </button>

                <button type="button" onClick={handleDownloadDoc}>
                  <img src={download} alt="download" />
                  <span className="btn-title">Download</span>
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {status?.toLowerCase() === "rejected" && rejectionRemarks && (
        <div className="reject-remark-cont">
          <div>
            <img src={rejectedIcon} alt="rejected" />
          </div>
          <div className="wrap-content">
            <span className="bold-text">Checker Rejection Remarks</span>
            <span className="remarks-val">{rejectionRemarks}</span>
          </div>
        </div>
      )}
    </>
  );
}

export default SingleRequestDetails;
